import React from 'react';
import { createPortal } from 'react-dom';
import { getClassMemberGradeLayoutsMap } from '../../services/classMembersService';
import { firstRecordGroupId } from '../../utils/classMemberRecordGroupUtils';
import {
  CLASS_MEMBERS_CHANGED_EVENT,
  emptyGradeLayout,
  loadClassMemberGradeLayoutFromStorage,
  type ClassMemberGradeLayout,
  type ClassMemberRow,
} from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import MemberProfileThumb from '../common/MemberProfileThumb';
import { SeatRotateIcon } from '../common/SeatRotateIcon';
import {
  loadSelectedClassGroupId,
  saveSelectedClassGroupId,
} from '../../utils/selectedClassGroupPreference';

export interface ProgramSlideDiceModalProps {
  open: boolean;
  onClose: () => void;
  /** 클래스 멤버 그룹별 번호 범위(시작 1 ~ 끝 = 멤버 수) 자동 설정 */
  memberLookup?: { workspaceId: string; gradeId: string } | null;
}

type DiceTab = 'number' | 'profile' | 'seat' | 'team';
type DiceMember = ClassMemberRow & { number: number };
type DiceSeatAssigned = { member: DiceMember; seatRow: number; seatCol: number };
type TeamDivideResult = DiceMember[][] | number[][];

const DICE_MODAL_LAST_TAB_KEY = 'cp-slide-dice:lastTab';
const DICE_SEAT_ROTATE_MAX = 15;
const MIN_W = 300;
const MIN_H = 350;
const MINI_HEADER_H = 34;
const MINI_MIN_W = 340;
const MINI_MIN_BODY_H = 126;
const MINI_MIN_H = MINI_HEADER_H + MINI_MIN_BODY_H;
const MINI_SCALE_MAX = 2;

function clampMiniScale(scale: number) {
  return Math.min(MINI_SCALE_MAX, Math.max(1, scale));
}

function miniOuterSize(scale: number) {
  const s = clampMiniScale(scale);
  return {
    width: MINI_MIN_W * s,
    height: MINI_HEADER_H + MINI_MIN_BODY_H * s,
    scale: s,
  };
}

function pickMiniScale(width: number, height: number, startScale: number) {
  const scaleW = width / MINI_MIN_W;
  const scaleH = (height - MINI_HEADER_H) / MINI_MIN_BODY_H;
  const picked = Math.abs(scaleW - startScale) >= Math.abs(scaleH - startScale) ? scaleW : scaleH;
  return clampMiniScale(picked);
}

const SCREEN_PAD = 8;

function clampOnScreen(x: number, y: number, width: number, height: number) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
  const maxX = Math.max(SCREEN_PAD, vw - width - SCREEN_PAD);
  const maxY = Math.max(SCREEN_PAD, vh - height - SCREEN_PAD);
  return {
    x: Math.min(maxX, Math.max(SCREEN_PAD, x)),
    y: Math.min(maxY, Math.max(SCREEN_PAD, y)),
  };
}

function initialNormalLayout() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
  const width = Math.max(MIN_W, Math.floor(vw * 0.8));
  const height = Math.max(MIN_H, Math.floor(vh * 0.8));
  return { width, height, ...clampOnScreen(Math.floor((vw - width) / 2), Math.floor((vh - height) / 2), width, height) };
}
const NUMBER_CARD_GAP = 16;
const NUMBER_CARD_GRID_PAD = 40;
const NUMBER_CARD_MIN = 72;
const NUMBER_CARD_MAX = 300;
const TEAM_BLOCK_GAP = 10;
const TEAM_RESULT_PAD = 24;
const TEAM_TITLE_H = 26;
const TEAM_TITLE_MB = 8;
const TEAM_NUM_GAP = 8;
const TEAM_NUM_MIN = 40;
const TEAM_NUM_MAX = 64;
const TEAM_BLOCK_PAD_MIN = 10;

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function readLastTab(): DiceTab {
  try {
    const saved = window.localStorage.getItem(DICE_MODAL_LAST_TAB_KEY);
    if (saved === 'number' || saved === 'profile' || saved === 'seat' || saved === 'team') return saved;
  } catch {
    /* ignore */
  }
  return 'number';
}

function numbersInRange(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
}

function buildRangeMembers(members: ClassMemberRow[] | null | undefined, start: number, end: number): DiceMember[] {
  if (!members?.length) return [];
  return members
    .map((m, i) => ({ ...m, number: start + i }))
    .filter((m) => m.number >= start && m.number <= end);
}

function buildDiceSeatLayout(items: DiceSeatAssigned[], isRotated: boolean, seatScale: number) {
  const assigned = items.map(({ member, seatRow, seatCol }) => ({
    member,
    seatRow: isRotated ? DICE_SEAT_ROTATE_MAX - seatRow : seatRow,
    seatCol: isRotated ? DICE_SEAT_ROTATE_MAX - seatCol : seatCol,
  }));

  let minRow = 0;
  let maxRow = DICE_SEAT_ROTATE_MAX;
  let minCol = 0;
  let maxCol = DICE_SEAT_ROTATE_MAX;
  if (assigned.length > 0) {
    const rows = assigned.map((a) => a.seatRow);
    const cols = assigned.map((a) => a.seatCol);
    minRow = Math.max(0, Math.min(...rows));
    maxRow = Math.min(DICE_SEAT_ROTATE_MAX, Math.max(...rows));
    minCol = Math.max(0, Math.min(...cols));
    maxCol = Math.min(DICE_SEAT_ROTATE_MAX, Math.max(...cols));
  }

  const rowCount = Math.max(2, maxRow - minRow + 2);
  const colCount = Math.max(2, maxCol - minCol + 2);
  const usedGridRows = new Set<number>();
  const usedGridCols = new Set<number>();
  assigned.forEach(({ seatRow, seatCol }) => {
    const rowIndex = seatRow - minRow;
    const colIndex = seatCol - minCol;
    [rowIndex, rowIndex + 1].forEach((idx) => {
      if (idx >= 0 && idx < rowCount) usedGridRows.add(idx);
    });
    [colIndex, colIndex + 1].forEach((idx) => {
      if (idx >= 0 && idx < colCount) usedGridCols.add(idx);
    });
  });

  const usedRowPx = 43 * seatScale;
  const emptyRowPx = 21 * seatScale;
  const usedColPx = 92 * seatScale;
  const emptyColPx = 46 * seatScale;

  const usedColsCount = usedGridCols.size;
  const rowTemplate = Array.from({ length: rowCount }, (_, idx) =>
    usedGridRows.has(idx) ? `${Math.round(usedRowPx)}px` : `${Math.round(emptyRowPx)}px`
  ).join(' ');
  const colTemplate = Array.from({ length: colCount }, (_, idx) =>
    usedGridCols.has(idx) ? `${Math.round(usedColPx)}px` : `${Math.round(emptyColPx)}px`
  ).join(' ');

  return { minRow, minCol, rowTemplate, colTemplate, assigned, colCount, usedColsCount };
}

function isMemberTeamResult(teams: TeamDivideResult): teams is DiceMember[][] {
  for (const team of teams) {
    for (const item of team) {
      return typeof item === 'object' && item !== null && 'id' in item;
    }
  }
  return false;
}

function SlideDiceStepIcon({ kind }: { kind: 'plus' | 'minus' }) {
  return (
    <svg className="cp-slide-dice-step-btn-icon" viewBox="0 0 24 24" aria-hidden>
      {kind === 'minus' ? (
        <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M5 12h14" />
      ) : (
        <>
          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M12 5v14" />
          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M5 12h14" />
        </>
      )}
    </svg>
  );
}

const ProgramSlideDiceModal: React.FC<ProgramSlideDiceModalProps> = ({ open, onClose, memberLookup = null }) => {
  const [selectedTab, setSelectedTab] = React.useState<DiceTab>(readLastTab);
  const [startNumber, setStartNumber] = React.useState(1);
  const [endNumber, setEndNumber] = React.useState(30);
  const [startInput, setStartInput] = React.useState('1');
  const [endInput, setEndInput] = React.useState('30');
  const [continuousCount, setContinuousCount] = React.useState(1);
  const [teamCount, setTeamCount] = React.useState(2);
  const [showTeamCountModal, setShowTeamCountModal] = React.useState(false);

  const [drawnNumbers, setDrawnNumbers] = React.useState<Set<number>>(() => new Set());
  const [numberResult, setNumberResult] = React.useState<number | null>(null);
  const [numberResults, setNumberResults] = React.useState<number[]>([]);

  const [profileResult, setProfileResult] = React.useState<DiceMember | null>(null);
  const [profileResults, setProfileResults] = React.useState<DiceMember[]>([]);

  const [seatResult, setSeatResult] = React.useState<DiceMember | null>(null);
  const [seatResults, setSeatResults] = React.useState<DiceMember[]>([]);
  const [isSeatRotated, setIsSeatRotated] = React.useState(false);

  const [teamResult, setTeamResult] = React.useState<TeamDivideResult | null>(null);

  const [isMinimized, setIsMinimized] = React.useState(false);
  const [isMaximized, setIsMaximized] = React.useState(false);
  const [position, setPosition] = React.useState({ x: 120, y: 72 });
  const [size, setSize] = React.useState({ width: 980, height: 760 });
  const [miniScale, setMiniScale] = React.useState(1);
  const normalSizeRef = React.useRef({ width: 980, height: 760 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = React.useState(false);
  const [resizeDirection, setResizeDirection] = React.useState<
    'nw' | 'ne' | 'sw' | 'se' | 'e' | 'w' | 'n' | 's' | null
  >(null);
  const [resizeStart, setResizeStart] = React.useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    scale: 1,
  });
  const [windowSize, setWindowSize] = React.useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 1920,
    h: typeof window !== 'undefined' ? window.innerHeight : 1080,
  });

  const [memberLayout, setMemberLayout] = React.useState<ClassMemberGradeLayout>(() => emptyGradeLayout());
  const [memberGroupPick, setMemberGroupPick] = React.useState('');
  const needsAutoGroupRef = React.useRef(false);

  const workspaceId = memberLookup?.workspaceId?.trim() ?? '';
  const gradeId = memberLookup?.gradeId?.trim() ?? '';

  const sortedMemberGroups = React.useMemo(
    () =>
      [...memberLayout.groups].sort(
        (a, b) =>
          a.sortOrder - b.sortOrder ||
          String(a.name ?? '').localeCompare(String(b.name ?? ''), 'ko') ||
          a.id.localeCompare(b.id)
      ),
    [memberLayout.groups]
  );

  const showMemberGroupScope = !!(memberLookup && sortedMemberGroups.length);

  const pickedGroupMembers = React.useMemo(() => {
    const gid = memberGroupPick.trim();
    if (!gid) return null;
    return memberLayout.membersByGroupId[gid] ?? null;
  }, [memberGroupPick, memberLayout.membersByGroupId]);

  const rangeMembers = React.useMemo(
    () => buildRangeMembers(pickedGroupMembers, startNumber, endNumber),
    [pickedGroupMembers, startNumber, endNumber]
  );

  const nameForDrawnNumber = React.useCallback(
    (drawn: number) => {
      if (!pickedGroupMembers?.length || !memberGroupPick.trim()) return '';
      const idx = drawn - startNumber;
      if (idx < 0 || idx >= pickedGroupMembers.length) return '';
      const member = pickedGroupMembers[idx];
      if (!member) return '';
      return displayClassMemberName(member).trim();
    },
    [pickedGroupMembers, memberGroupPick, startNumber]
  );

  const loadMemberLayout = React.useCallback(async () => {
    const sid = memberLookup?.workspaceId?.trim();
    const gid = memberLookup?.gradeId?.trim();
    if (!sid || !gid) {
      setMemberLayout(emptyGradeLayout());
      return;
    }
    setMemberLayout(loadClassMemberGradeLayoutFromStorage(sid, gid) ?? emptyGradeLayout());
    try {
      await getClassMemberGradeLayoutsMap(sid, [gid]);
    } catch {
      /* ignore */
    }
    setMemberLayout(loadClassMemberGradeLayoutFromStorage(sid, gid) ?? emptyGradeLayout());
  }, [memberLookup]);

  React.useEffect(() => {
    if (!open) {
      needsAutoGroupRef.current = false;
      return;
    }
    needsAutoGroupRef.current = true;
  }, [open, workspaceId, gradeId]);

  React.useEffect(() => {
    if (!open || !memberLookup?.workspaceId?.trim() || !memberLookup?.gradeId?.trim()) return undefined;
    void loadMemberLayout();
    const sid = memberLookup.workspaceId.trim();
    const gid = memberLookup.gradeId.trim();
    const onMembersChanged = (e: Event) => {
      const d = (e as CustomEvent<{ workspaceId?: string; gradeId?: string }>).detail;
      if (d?.workspaceId === sid && d?.gradeId === gid) void loadMemberLayout();
    };
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onMembersChanged);
    return () => window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onMembersChanged);
  }, [open, memberLookup, loadMemberLayout]);

  const poolSize = Math.max(0, endNumber - startNumber + 1);
  const teamPoolSize = rangeMembers.length > 0 ? rangeMembers.length : poolSize;
  const maxTeamCount = Math.max(2, Math.floor(teamPoolSize / 2));

  const availableNumbers = React.useMemo(() => {
    const arr: number[] = [];
    for (let i = startNumber; i <= endNumber; i += 1) {
      if (!drawnNumbers.has(i)) arr.push(i);
    }
    return arr;
  }, [startNumber, endNumber, drawnNumbers]);

  const availableProfiles = React.useMemo(
    () => rangeMembers.filter((m) => !drawnNumbers.has(m.number)),
    [rangeMembers, drawnNumbers]
  );

  const seatAssignedItems = React.useMemo((): DiceSeatAssigned[] => {
    return rangeMembers
      .filter((m) => m.seatPosition && typeof m.seatPosition.row === 'number' && typeof m.seatPosition.col === 'number')
      .map((m) => ({
        member: m,
        seatRow: m.seatPosition!.row,
        seatCol: m.seatPosition!.col,
      }));
  }, [rangeMembers]);

  const availableSeatMembers = React.useMemo(
    () => seatAssignedItems.map((i) => i.member).filter((m) => !drawnNumbers.has(m.number)),
    [seatAssignedItems, drawnNumbers]
  );

  React.useEffect(() => {
    if (!open) return;
    setIsMinimized(false);
    setIsMaximized(false);
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
    const next = initialNormalLayout();
    setSize({ width: next.width, height: next.height });
    setPosition({ x: next.x, y: next.y });
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    try {
      window.localStorage.setItem(DICE_MODAL_LAST_TAB_KEY, selectedTab);
    } catch {
      /* ignore */
    }
  }, [open, selectedTab]);

  React.useEffect(() => {
    if (!open || !isMaximized) return;
    const onResize = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, isMaximized]);

  React.useEffect(() => {
    if (!open || !isMinimized) return;
    const keepMiniOnScreen = () => {
      const mini = miniOuterSize(miniScale);
      setPosition((pos) => clampOnScreen(pos.x, pos.y, mini.width, mini.height));
    };
    keepMiniOnScreen();
    window.addEventListener('resize', keepMiniOnScreen);
    return () => window.removeEventListener('resize', keepMiniOnScreen);
  }, [open, isMinimized, miniScale]);

  React.useEffect(() => {
    if (teamCount > maxTeamCount) setTeamCount(maxTeamCount);
  }, [maxTeamCount, teamCount]);

  const miniOuter = miniOuterSize(miniScale);
  const modalWidth = isMaximized ? windowSize.w : isMinimized ? miniOuter.width : size.width;
  const modalHeight = isMaximized ? windowSize.h : isMinimized ? miniOuter.height : size.height;
  const modalLeft = isMaximized ? 0 : position.x;
  const modalTop = isMaximized ? 0 : position.y;

  const seatLayoutMetaAt1 = React.useMemo(
    () => buildDiceSeatLayout(seatAssignedItems, isSeatRotated, 1),
    [seatAssignedItems, isSeatRotated]
  );

  const seatScaleWanted = Math.min(2.25, Math.min(modalWidth / 300, modalHeight / 350));
  const seatColGapPx = 2;
  const gridWidthAt1 =
    seatLayoutMetaAt1.usedColsCount * 92 +
    (seatLayoutMetaAt1.colCount - seatLayoutMetaAt1.usedColsCount) * 46 +
    Math.max(0, seatLayoutMetaAt1.colCount - 1) * seatColGapPx +
    8;
  const availableWidthForGrid = Math.max(8, modalWidth - 96);
  const maxSeatScaleForWidth = gridWidthAt1 > 0 ? availableWidthForGrid / gridWidthAt1 : seatScaleWanted;
  const seatScale = Math.max(0.08, Math.min(seatScaleWanted, maxSeatScaleForWidth));
  const seatNarrowLayout = modalWidth <= 800;

  const diceSeatLayout = React.useMemo(
    () => buildDiceSeatLayout(seatAssignedItems, isSeatRotated, seatScale),
    [seatAssignedItems, isSeatRotated, seatScale]
  );

  const currentSeatHighlightIds = React.useMemo(() => {
    if (seatResults.length > 0) return new Set(seatResults.map((m) => m.id));
    if (seatResult) return new Set([seatResult.id]);
    return new Set<string>();
  }, [seatResult, seatResults]);

  const hasAnySeatDraw = drawnNumbers.size > 0;

  const diceBodyRef = React.useRef<HTMLDivElement>(null);
  const diceChromeRef = React.useRef<HTMLDivElement>(null);
  const numberGridRef = React.useRef<HTMLDivElement>(null);
  const teamResultRef = React.useRef<HTMLDivElement>(null);

  const numberCards = React.useMemo(() => {
    if (numberResults.length > 0) return numberResults;
    if (numberResult !== null) return [numberResult];
    return [];
  }, [numberResult, numberResults]);

  const profileCards = React.useMemo(() => {
    if (profileResults.length > 0) return profileResults;
    if (profileResult) return [profileResult];
    return [];
  }, [profileResult, profileResults]);

  const layoutNumberCards = React.useCallback(() => {
    const body = diceBodyRef.current;
    const chrome = diceChromeRef.current;
    const grid = numberGridRef.current;
    const count = numberCards.length;
    if (!body || !chrome || !grid || count === 0) return;

    const availableH = Math.max(0, body.clientHeight - chrome.offsetHeight - 12);
    const availableW = Math.max(0, body.clientWidth - 40);

    let cols = Math.max(1, Math.ceil(count / 2));
    const maxColsByWidth = Math.max(
      1,
      Math.floor((availableW - NUMBER_CARD_GRID_PAD + NUMBER_CARD_GAP) / (NUMBER_CARD_MIN + NUMBER_CARD_GAP))
    );
    cols = Math.min(cols, count, maxColsByWidth);

    const rowCount = Math.ceil(count / cols);
    const visibleRows = Math.min(2, rowCount);

    const sizeByH =
      (availableH - NUMBER_CARD_GRID_PAD - NUMBER_CARD_GAP * (visibleRows - 1)) / visibleRows;
    const sizeByW = (availableW - NUMBER_CARD_GRID_PAD - NUMBER_CARD_GAP * (cols - 1)) / cols;
    let cardSize = Math.min(sizeByH, sizeByW, NUMBER_CARD_MAX);
    cardSize = Math.max(NUMBER_CARD_MIN, Math.floor(cardSize));

    grid.style.setProperty('--dice-card-size', `${cardSize}px`);
    grid.style.setProperty('--dice-card-font', `${Math.max(42, Math.floor(cardSize * 0.58))}px`);
  }, [numberCards.length]);

  const layoutTeamBlocks = React.useCallback(() => {
    const body = diceBodyRef.current;
    const chrome = diceChromeRef.current;
    const container = teamResultRef.current;
    const teams = teamResult;
    if (!body || !chrome || !container || !teams || teams.length === 0) return;
    if (isMemberTeamResult(teams)) return;

    const availableH = Math.max(0, body.clientHeight - chrome.offsetHeight - 12);
    const availableW = Math.max(0, body.clientWidth - 40);
    const visibleTeams = Math.min(2, teams.length);
    const maxMembers = Math.max(...teams.map((t) => t.length), 1);

    const blockBudget =
      (availableH - TEAM_RESULT_PAD - TEAM_BLOCK_GAP * (visibleTeams - 1)) / visibleTeams;

    let cols = Math.max(1, Math.ceil(maxMembers / 2));
    const maxCols = Math.max(
      1,
      Math.floor((availableW - 48 + TEAM_NUM_GAP) / (TEAM_NUM_MIN + TEAM_NUM_GAP))
    );
    cols = Math.min(cols, maxMembers, maxCols);

    const numRows = Math.ceil(maxMembers / cols);
    const gridBudget = Math.max(TEAM_NUM_MIN, blockBudget - TEAM_TITLE_H - TEAM_TITLE_MB - TEAM_BLOCK_PAD_MIN * 2);
    const numSizeByH = (gridBudget - TEAM_NUM_GAP * (numRows - 1)) / numRows;
    const numSizeByW = (availableW - 48 - TEAM_NUM_GAP * (cols - 1)) / cols;
    let numSize = Math.min(numSizeByH, numSizeByW, TEAM_NUM_MAX);
    numSize = Math.max(TEAM_NUM_MIN, Math.floor(numSize));

    const blockPad = Math.max(
      TEAM_BLOCK_PAD_MIN,
      Math.floor(
        (blockBudget - TEAM_TITLE_H - TEAM_TITLE_MB - numRows * numSize - TEAM_NUM_GAP * (numRows - 1)) / 2
      )
    );

    container.style.setProperty('--dice-team-gap', `${TEAM_BLOCK_GAP}px`);
    container.style.setProperty('--dice-team-block-pad', `${blockPad}px`);
    container.style.setProperty('--dice-team-title-font', `${Math.max(12, Math.min(15, Math.floor(blockBudget * 0.14)))}px`);
    container.style.setProperty('--dice-team-title-gap', `${Math.max(6, Math.floor(blockPad * 0.5))}px`);
    container.style.setProperty('--dice-team-num-gap', `${TEAM_NUM_GAP}px`);
    container.style.setProperty('--dice-team-num-size', `${numSize}px`);
    container.style.setProperty('--dice-team-num-font', `${Math.max(16, Math.floor(numSize * 0.4))}px`);
  }, [teamResult]);

  const layoutDiceContent = React.useCallback(() => {
    if (selectedTab === 'number' && numberCards.length > 0) layoutNumberCards();
    else if (selectedTab === 'team' && teamResult && teamResult.length > 0) layoutTeamBlocks();
  }, [selectedTab, numberCards.length, teamResult, layoutNumberCards, layoutTeamBlocks]);

  React.useLayoutEffect(() => {
    if (!open || isMinimized) return;
    const hasNumber = selectedTab === 'number' && numberCards.length > 0;
    const hasTeam = selectedTab === 'team' && teamResult && teamResult.length > 0;
    if (!hasNumber && !hasTeam) return;

    layoutDiceContent();
    const body = diceBodyRef.current;
    const chrome = diceChromeRef.current;
    if (!body) return;
    const ro = new ResizeObserver(() => layoutDiceContent());
    ro.observe(body);
    if (chrome) ro.observe(chrome);
    return () => ro.disconnect();
  }, [
    open,
    isMinimized,
    selectedTab,
    numberCards.length,
    teamResult,
    layoutDiceContent,
    modalWidth,
    modalHeight,
  ]);

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (isMaximized) return;
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.tagName === 'BUTTON' || target.closest('input')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  React.useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const next = { x: e.clientX - dragStart.x, y: e.clientY - dragStart.y };
      if (isMinimized) {
        setPosition(clampOnScreen(next.x, next.y, miniOuter.width, miniOuter.height));
      } else {
        setPosition(next);
      }
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, dragStart, isMinimized, miniOuter.height, miniOuter.width]);

  const handleResizeMouseDown = (
    e: React.MouseEvent,
    direction: 'nw' | 'ne' | 'sw' | 'se' | 'e' | 'w' | 'n' | 's'
  ) => {
    if (isMaximized) return;
    e.stopPropagation();
    setResizeDirection(direction);
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: isMinimized ? miniOuter.width : size.width,
      height: isMinimized ? miniOuter.height : size.height,
      left: position.x,
      top: position.y,
      scale: miniScale,
    });
  };

  React.useEffect(() => {
    if (!isResizing || !resizeDirection) return;
    const onMove = (e: MouseEvent) => {
      const minW = isMinimized ? MINI_MIN_W : MIN_W;
      const minH = isMinimized ? MINI_MIN_H : MIN_H;
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.left;
      let newY = resizeStart.top;
      switch (resizeDirection) {
        case 'nw':
          newWidth = Math.max(minW, resizeStart.width - deltaX);
          newHeight = Math.max(minH, resizeStart.height - deltaY);
          break;
        case 'ne':
          newWidth = Math.max(minW, resizeStart.width + deltaX);
          newHeight = Math.max(minH, resizeStart.height - deltaY);
          break;
        case 'sw':
          newWidth = Math.max(minW, resizeStart.width - deltaX);
          newHeight = Math.max(minH, resizeStart.height + deltaY);
          break;
        case 'se':
          newWidth = Math.max(minW, resizeStart.width + deltaX);
          newHeight = Math.max(minH, resizeStart.height + deltaY);
          break;
        case 'e':
          newWidth = Math.max(minW, resizeStart.width + deltaX);
          newHeight = resizeStart.height;
          break;
        case 'w':
          newWidth = Math.max(minW, resizeStart.width - deltaX);
          newHeight = resizeStart.height;
          break;
        case 'n':
          newWidth = resizeStart.width;
          newHeight = Math.max(minH, resizeStart.height - deltaY);
          break;
        case 's':
          newWidth = resizeStart.width;
          newHeight = Math.max(minH, resizeStart.height + deltaY);
          break;
        default:
          break;
      }
      if (isMinimized) {
        const locked = miniOuterSize(pickMiniScale(newWidth, newHeight, resizeStart.scale));
        newWidth = locked.width;
        newHeight = locked.height;
        setMiniScale(locked.scale);
      } else {
        setSize({ width: newWidth, height: newHeight });
      }
      if (resizeDirection === 'nw' || resizeDirection === 'sw' || resizeDirection === 'w') {
        newX = resizeStart.left + (resizeStart.width - newWidth);
      }
      if (resizeDirection === 'nw' || resizeDirection === 'ne' || resizeDirection === 'n') {
        newY = resizeStart.top + (resizeStart.height - newHeight);
      }
      if (isMinimized) {
        const clamped = clampOnScreen(newX, newY, newWidth, newHeight);
        newX = clamped.x;
        newY = clamped.y;
      }
      setPosition({ x: newX, y: newY });
    };
    const onUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isMinimized, isResizing, resizeDirection, resizeStart]);

  const applyStartNumber = React.useCallback(
    (value: number) => {
      const safe = Math.max(1, Math.min(999, value));
      const next = Math.min(safe, endNumber);
      setStartNumber(next);
      setStartInput(String(next));
    },
    [endNumber]
  );

  const applyEndNumber = React.useCallback(
    (value: number) => {
      const safe = Math.max(1, Math.min(999, value));
      const next = Math.max(startNumber, safe);
      setEndNumber(next);
      setEndInput(String(next));
      if (startNumber > next) {
        setStartNumber(next);
        setStartInput(String(next));
      }
    },
    [startNumber]
  );

  const commitStartInput = React.useCallback(() => {
    const raw = startInput.trim();
    if (raw === '') {
      applyStartNumber(1);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setStartInput(String(startNumber));
      return;
    }
    applyStartNumber(parsed);
  }, [applyStartNumber, startInput, startNumber]);

  const commitEndInput = React.useCallback(() => {
    const raw = endInput.trim();
    if (raw === '') {
      applyEndNumber(30);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setEndInput(String(endNumber));
      return;
    }
    applyEndNumber(parsed);
  }, [applyEndNumber, endInput, endNumber]);

  const handleRangeInputChange = (raw: string, setter: (value: string) => void) => {
    if (raw === '' || /^\d+$/.test(raw)) setter(raw);
  };

  const applyMemberGroupRange = React.useCallback(
    (groupId: string) => {
      setMemberGroupPick(groupId);
      if (workspaceId && gradeId) saveSelectedClassGroupId(workspaceId, gradeId, groupId);
      if (!groupId.trim()) return;
      const count = (memberLayout.membersByGroupId[groupId] ?? []).length;
      const end = Math.max(1, Math.min(999, count));
      setStartNumber(1);
      setStartInput('1');
      setEndNumber(end);
      setEndInput(String(end));
    },
    [memberLayout.membersByGroupId, workspaceId, gradeId]
  );

  React.useEffect(() => {
    if (!open || !needsAutoGroupRef.current) return;
    if (!sortedMemberGroups.length) return;
    const ids = new Set(sortedMemberGroups.map((g) => g.id));
    const remembered =
      workspaceId && gradeId ? loadSelectedClassGroupId(workspaceId, gradeId) ?? '' : '';
    const preferred =
      (remembered && ids.has(remembered) ? remembered : '') || firstRecordGroupId(memberLayout);
    if (!preferred) return;
    applyMemberGroupRange(preferred);
    needsAutoGroupRef.current = false;
  }, [open, sortedMemberGroups, memberLayout, workspaceId, gradeId, applyMemberGroupRange]);

  const applySharedPick = React.useCallback(
    (drawn: number[]) => {
      const drawnSet = new Set(drawnNumbers);
      drawn.forEach((n) => drawnSet.add(n));
      setDrawnNumbers(drawnSet);
      const members = drawn
        .map((n) => rangeMembers.find((m) => m.number === n))
        .filter((m): m is DiceMember => Boolean(m));
      if (drawn.length <= 1) {
        setNumberResult(drawn[0] ?? null);
        setNumberResults([]);
        setProfileResult(members[0] ?? null);
        setProfileResults([]);
        setSeatResult(members[0] ?? null);
        setSeatResults([]);
        return;
      }
      setNumberResult(null);
      setNumberResults(drawn);
      setProfileResult(null);
      setProfileResults(members);
      setSeatResult(null);
      setSeatResults(members);
    },
    [drawnNumbers, rangeMembers]
  );

  const handleDrawNumber = () => {
    if (availableNumbers.length === 0) return;
    const count = Math.min(continuousCount, availableNumbers.length);
    const drawn: number[] = [];
    const drawnSet = new Set(drawnNumbers);
    for (let i = 0; i < count; i += 1) {
      const remaining = availableNumbers.filter((n) => !drawnSet.has(n));
      if (remaining.length === 0) break;
      const pick = remaining[Math.floor(Math.random() * remaining.length)]!;
      drawn.push(pick);
      drawnSet.add(pick);
    }
    applySharedPick(drawn);
  };

  const handleDrawProfile = (opts?: { once?: boolean }) => {
    if (!pickedGroupMembers?.length || availableProfiles.length === 0) return;
    const count = Math.min(opts?.once ? 1 : continuousCount, availableProfiles.length);
    const drawnMembers: DiceMember[] = [];
    const drawnSet = new Set(drawnNumbers);
    for (let i = 0; i < count; i += 1) {
      const remaining = availableProfiles.filter((m) => !drawnSet.has(m.number));
      if (remaining.length === 0) break;
      const selected = remaining[Math.floor(Math.random() * remaining.length)]!;
      drawnMembers.push(selected);
      drawnSet.add(selected.number);
    }
    applySharedPick(drawnMembers.map((m) => m.number));
  };

  const handleDrawSeat = () => {
    if (!pickedGroupMembers?.length || availableSeatMembers.length === 0) return;
    const count = Math.min(continuousCount, availableSeatMembers.length);
    const drawnMembers: DiceMember[] = [];
    const drawnSet = new Set(drawnNumbers);
    for (let i = 0; i < count; i += 1) {
      const remaining = availableSeatMembers.filter((m) => !drawnSet.has(m.number));
      if (remaining.length === 0) break;
      const selected = remaining[Math.floor(Math.random() * remaining.length)]!;
      drawnMembers.push(selected);
      drawnSet.add(selected.number);
    }
    applySharedPick(drawnMembers.map((m) => m.number));
  };

  const handleDivideTeams = () => {
    const safeTeamCount = Math.max(2, Math.min(maxTeamCount, teamCount));
    if (rangeMembers.length > 0) {
      const shuffled = [...rangeMembers].sort(() => Math.random() - 0.5);
      const teams: DiceMember[][] = Array.from({ length: safeTeamCount }, () => []);
      shuffled.forEach((member, index) => {
        teams[index % safeTeamCount]!.push(member);
      });
      setTeamResult(teams);
      setShowTeamCountModal(false);
      return;
    }
    const pool = numbersInRange(startNumber, endNumber);
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    const teams: number[][] = Array.from({ length: safeTeamCount }, () => []);
    shuffled.forEach((num, index) => {
      teams[index % safeTeamCount]!.push(num);
    });
    setTeamResult(teams);
    setShowTeamCountModal(false);
  };

  const handleResetTab = () => {
    if (selectedTab === 'number' || selectedTab === 'profile' || selectedTab === 'seat') {
      setDrawnNumbers(new Set());
      setNumberResult(null);
      setNumberResults([]);
      setProfileResult(null);
      setProfileResults([]);
      setSeatResult(null);
      setSeatResults([]);
      return;
    }
    setTeamResult(null);
  };

  const getSeatCardVisual = (member: DiceMember) => {
    if (currentSeatHighlightIds.has(member.id)) return 'highlight' as const;
    if (!hasAnySeatDraw) return 'idle' as const;
    if (drawnNumbers.has(member.number)) return 'drawn' as const;
    return 'faded' as const;
  };

  const showContinuous =
    selectedTab === 'number' || selectedTab === 'profile' || selectedTab === 'seat';

  const remainingForTab =
    selectedTab === 'number'
      ? availableNumbers.length
      : selectedTab === 'profile'
        ? pickedGroupMembers?.length
          ? availableProfiles.length
          : 0
        : selectedTab === 'seat'
          ? pickedGroupMembers?.length
            ? availableSeatMembers.length
            : 0
          : null;

  const drawProgressGauge = React.useMemo(() => {
    if (selectedTab === 'team') {
      return { total: 0, drawn: 0, pct: 0, ariaLabel: '팀 나누기 진행 없음' };
    }
    if (selectedTab === 'number') {
      const total = poolSize;
      const drawn = Math.max(0, total - availableNumbers.length);
      return {
        total,
        drawn,
        pct: total > 0 ? Math.min(100, (drawn / total) * 100) : 0,
        ariaLabel: total > 0 ? `전체 ${total}개 중 ${drawn}개 뽑음, ${availableNumbers.length}개 남음` : '뽑기 진행 없음',
      };
    }
    if (selectedTab === 'profile') {
      const total = rangeMembers.length;
      const drawn = Math.max(0, total - availableProfiles.length);
      return {
        total,
        drawn,
        pct: total > 0 ? Math.min(100, (drawn / total) * 100) : 0,
        ariaLabel:
          total > 0
            ? `전체 ${total}명 중 ${drawn}명 뽑음, ${availableProfiles.length}명 남음`
            : '뽑기 진행 없음',
      };
    }
    const total = seatAssignedItems.length;
    const drawn = Math.max(0, total - availableSeatMembers.length);
    return {
      total,
      drawn,
      pct: total > 0 ? Math.min(100, (drawn / total) * 100) : 0,
      ariaLabel:
        total > 0
          ? `전체 ${total}명 중 ${drawn}명 뽑음, ${availableSeatMembers.length}명 남음`
          : '뽑기 진행 없음',
    };
  }, [
    availableNumbers.length,
    availableProfiles.length,
    availableSeatMembers.length,
    poolSize,
    rangeMembers.length,
    seatAssignedItems.length,
    selectedTab,
  ]);

  const rollAriaLabel =
    selectedTab === 'number'
      ? '번호 뽑기'
      : selectedTab === 'profile'
        ? '프로필 뽑기'
        : selectedTab === 'seat'
          ? '자리 뽑기'
          : '팀 나누기';

  const enterProfileMiniMode = React.useCallback(() => {
    setSelectedTab('profile');
    if (!isMinimized) {
      normalSizeRef.current = { width: size.width, height: size.height };
    }
    const mini = miniOuterSize(1);
    setMiniScale(1);
    setPosition((pos) => clampOnScreen(pos.x, pos.y, mini.width, mini.height));
    setIsMinimized(true);
    setIsMaximized(false);
  }, [isMinimized, size.height, size.width]);

  const restoreFromMiniMode = React.useCallback(() => {
    const next = initialNormalLayout();
    setSize({ width: next.width, height: next.height });
    setPosition({ x: next.x, y: next.y });
    setIsMinimized(false);
    setIsMaximized(false);
  }, []);

  const miniProfileMember = profileResult ?? profileResults[0] ?? null;

  if (!open) return null;

  return createPortal(
    <div
      className={
        'cp-program-slide-timer-modal-backdrop cp-slide-dice-overlay' +
        (isMinimized ? ' is-minimized' : '')
      }
      data-tutorial-pres="dice-modal"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isMinimized) onClose();
      }}
    >
      <div
        className={
          'cp-program-slide-timer-modal cp-program-slide-dice-modal cp-slide-dice-modal' +
          (isMinimized ? ' is-minimized' : '') +
          (isMaximized ? ' is-maximized' : '')
        }
        style={{
          left: modalLeft,
          top: modalTop,
          width: modalWidth,
          height: modalHeight,
          ...(isMinimized
            ? ({
                '--mini-scale': String(miniScale),
                '--mini-base-w': `${MINI_MIN_W}px`,
                '--mini-base-body-h': `${MINI_MIN_BODY_H}px`,
              } as React.CSSProperties)
            : null),
        }}
      >
        <div className="cp-program-slide-timer-modal-header cp-slide-dice-header" onMouseDown={handleHeaderMouseDown}>
          <h2 id="cp-slide-dice-modal-title" className="cp-program-slide-timer-modal-title">
            뽑기
          </h2>
          <div className="cp-slide-dice-header-controls">
            <button
              type="button"
              className="cp-slide-dice-window-btn"
              onClick={enterProfileMiniMode}
              disabled={isMinimized}
              title="프로필 뽑기 최소화"
              aria-label="프로필 뽑기 최소화"
            >
              <img className="cp-slide-dice-window-btn-icon" src="/Minus.svg" alt="" />
            </button>
            <button
              type="button"
              className="cp-slide-dice-window-btn"
              onClick={() => {
                if (isMinimized) restoreFromMiniMode();
                else if (isMaximized) setIsMaximized(false);
                else setIsMaximized(true);
              }}
              title={isMinimized ? '중간 모드' : isMaximized ? '중간 모드' : '최대 확장'}
              aria-label={isMinimized ? '중간 모드' : isMaximized ? '중간 모드' : '최대 확장'}
            >
              <img className="cp-slide-dice-window-btn-icon" src="/maxwindow.svg" alt="" />
            </button>
          </div>
          <button type="button" className="cp-program-slide-timer-modal-close" onClick={onClose} aria-label="닫기">
            <img className="cp-slide-dice-window-btn-icon cp-program-slide-timer-modal-close-icon" src="/modal-close.svg" alt="" />
          </button>
        </div>

        <div
          ref={diceBodyRef}
          className={'cp-slide-dice-body' + (isMinimized ? ' is-minimized' : '')}
        >
          {isMinimized ? (
            <div className="cp-slide-dice-mini-profile" onMouseDown={handleHeaderMouseDown}>
              {miniProfileMember ? (
                <div className="cp-slide-dice-mini-profile-image">
                  <MemberProfileThumb
                    member={miniProfileMember}
                    workspaceId={workspaceId}
                    gradeId={gradeId}
                    alt={displayClassMemberName(miniProfileMember).trim() || '프로필'}
                  />
                </div>
              ) : (
                <div className="cp-slide-dice-mini-profile-image is-empty" aria-hidden />
              )}
              <div className="cp-slide-dice-mini-profile-main">
                <div className="cp-slide-dice-mini-profile-top">
                  <div className="cp-slide-dice-mini-profile-info">
                    {miniProfileMember ? (
                      <>
                        <span className="cp-slide-dice-mini-profile-num">{pad2(miniProfileMember.number)}</span>
                        <span className="cp-slide-dice-mini-profile-name">
                          {displayClassMemberName(miniProfileMember).trim() || '이름 없음'}
                        </span>
                      </>
                    ) : (
                      <span className="cp-slide-dice-mini-profile-empty">
                        {!pickedGroupMembers?.length
                          ? '멤버 그룹을 선택한 뒤 펼쳐 주세요.'
                          : availableProfiles.length === 0
                            ? '모두 뽑았습니다. 리셋으로 초기화해 주세요.'
                            : '주사위를 눌러 뽑아 보세요.'}
                      </span>
                    )}
                  </div>
                  <div className="cp-slide-dice-mini-profile-actions">
                    <button
                      type="button"
                      className="cp-slide-dice-mini-profile-reset"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={handleResetTab}
                      aria-label="뽑기 초기화"
                      title="초기화"
                    >
                      <img src="/reset.svg" alt="" />
                    </button>
                    <button
                      type="button"
                      className="cp-slide-dice-roll-btn cp-slide-dice-mini-profile-roll"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => handleDrawProfile({ once: true })}
                      disabled={!pickedGroupMembers?.length || availableProfiles.length === 0}
                      aria-label="프로필 뽑기"
                      title="프로필 뽑기"
                    >
                      <img src="/Dice.svg" alt="" />
                    </button>
                  </div>
                </div>
                <div
                  className="cp-slide-dice-mini-profile-gauge"
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={Math.max(1, rangeMembers.length)}
                  aria-valuenow={drawnNumbers.size}
                  aria-label={
                    rangeMembers.length
                      ? `전체 ${rangeMembers.length}명 중 ${drawnNumbers.size}명 뽑김, ${availableProfiles.length}명 남음`
                      : '뽑기 진행 없음'
                  }
                >
                  <div
                    className="cp-slide-dice-mini-profile-gauge-fill"
                    style={{
                      width: rangeMembers.length
                        ? `${Math.min(100, (drawnNumbers.size / rangeMembers.length) * 100)}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="cp-slide-dice-form">
              <div ref={diceChromeRef} className="cp-slide-dice-chrome">
                <div className="cp-slide-dice-top-controls-row">
                  <div className="cp-slide-dice-top-controls-left">
                    {showMemberGroupScope ? (
                      <select
                        id="cp-slide-dice-member-group-select"
                        className="cp-slide-dice-member-scope-select"
                        aria-label="멤버 그룹"
                        value={memberGroupPick}
                        onChange={(e) => applyMemberGroupRange(e.target.value)}
                      >
                        <option value="">그룹 선택 없음</option>
                        {sortedMemberGroups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {(g.name ?? '').trim() || '그룹'}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  <div className="cp-slide-dice-top-controls-center">
                    <div className="cp-slide-dice-number-row cp-slide-dice-number-row--center-range">
                <div className="cp-slide-dice-number-group">
                  <div className="cp-slide-dice-number-field">
                    <span className="cp-slide-dice-number-label">시작</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="cp-slide-dice-number-input"
                      value={startInput}
                      aria-label="시작 번호"
                      onChange={(e) => handleRangeInputChange(e.target.value, setStartInput)}
                      onBlur={commitStartInput}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitStartInput();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                    />
                    <div className="cp-slide-dice-number-actions">
                      <button
                        type="button"
                        className="cp-slide-dice-icon-btn"
                        onClick={() => applyStartNumber(1)}
                        title="초기화"
                      >
                        <img src="/reset.svg" alt="" />
                      </button>
                      <button
                        type="button"
                        className="cp-slide-dice-step-btn"
                        onClick={() => applyStartNumber(startNumber - 1)}
                        disabled={startNumber <= 1}
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="cp-slide-dice-step-btn"
                        onClick={() => applyStartNumber(startNumber + 1)}
                        disabled={startNumber >= endNumber}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className="cp-slide-dice-number-group">
                  <div className="cp-slide-dice-number-field">
                    <span className="cp-slide-dice-number-label">끝</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="cp-slide-dice-number-input"
                      value={endInput}
                      aria-label="끝 번호"
                      onChange={(e) => handleRangeInputChange(e.target.value, setEndInput)}
                      onBlur={commitEndInput}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitEndInput();
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                    />
                    <div className="cp-slide-dice-number-actions">
                      <button
                        type="button"
                        className="cp-slide-dice-icon-btn"
                        onClick={() => applyEndNumber(30)}
                        title="초기화"
                      >
                        <img src="/reset.svg" alt="" />
                      </button>
                      <button
                        type="button"
                        className="cp-slide-dice-step-btn"
                        onClick={() => applyEndNumber(endNumber - 1)}
                        disabled={endNumber <= startNumber}
                      >
                        -
                      </button>
                      <button
                        type="button"
                        className="cp-slide-dice-step-btn"
                        onClick={() => applyEndNumber(endNumber + 1)}
                        disabled={endNumber >= 999}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                    </div>
                  </div>
                  <div className="cp-slide-dice-top-controls-right" aria-hidden="true" />
                </div>

              <div className="cp-slide-dice-tab-row">
                <button
                  type="button"
                  className={'cp-program-slide-timer-preset cp-slide-dice-tab-btn' + (selectedTab === 'number' ? ' is-active' : '')}
                  onClick={() => setSelectedTab('number')}
                >
                  번호 뽑기
                </button>
                <button
                  type="button"
                  className={'cp-program-slide-timer-preset cp-slide-dice-tab-btn' + (selectedTab === 'profile' ? ' is-active' : '')}
                  onClick={() => setSelectedTab('profile')}
                >
                  프로필 뽑기
                </button>
                <button
                  type="button"
                  className={'cp-program-slide-timer-preset cp-slide-dice-tab-btn' + (selectedTab === 'seat' ? ' is-active' : '')}
                  onClick={() => setSelectedTab('seat')}
                >
                  자리 뽑기
                </button>
                <button
                  type="button"
                  className={'cp-program-slide-timer-preset cp-slide-dice-tab-btn' + (selectedTab === 'team' ? ' is-active' : '')}
                  onClick={() => setSelectedTab('team')}
                >
                  팀 나누기
                </button>
              </div>

                {selectedTab !== 'team' ? (
                  <div
                    className="cp-slide-dice-draw-gauge cp-slide-dice-mini-profile-gauge"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={Math.max(1, drawProgressGauge.total)}
                    aria-valuenow={drawProgressGauge.drawn}
                    aria-label={drawProgressGauge.ariaLabel}
                  >
                    <div
                      className="cp-slide-dice-draw-gauge-fill cp-slide-dice-mini-profile-gauge-fill"
                      style={{ width: `${drawProgressGauge.pct}%` }}
                    />
                  </div>
                ) : null}

                <div className="cp-slide-dice-result-header">
                  <div className="cp-slide-dice-result-left">
                    {showContinuous ? (
                      <div className="cp-slide-dice-continuous">
                        <div className="cp-slide-dice-continuous-top">
                          <span className="cp-slide-dice-continuous-label">연속</span>
                          <input
                            type="number"
                            className="cp-slide-dice-continuous-input"
                            value={continuousCount}
                            min={1}
                            max={100}
                            aria-label="연속 뽑기 개수"
                            onChange={(e) =>
                              setContinuousCount(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))
                            }
                          />
                        </div>
                        <div className="cp-slide-dice-continuous-bottom">
                          <button
                            type="button"
                            className="cp-slide-dice-step-btn"
                            onClick={() => setContinuousCount((v) => Math.max(1, v - 1))}
                            disabled={continuousCount <= 1}
                          >
                            -
                          </button>
                          <button
                            type="button"
                            className="cp-slide-dice-step-btn"
                            onClick={() => setContinuousCount((v) => Math.min(100, v + 1))}
                            disabled={continuousCount >= 100}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="cp-slide-dice-result-center">
                    <button
                      type="button"
                      className="cp-slide-dice-roll-btn"
                      onClick={() => {
                        if (selectedTab === 'number') handleDrawNumber();
                        else if (selectedTab === 'profile') handleDrawProfile();
                        else if (selectedTab === 'seat') handleDrawSeat();
                        else setShowTeamCountModal(true);
                      }}
                      aria-label={rollAriaLabel}
                    >
                      <img src="/Dice.svg" alt="" />
                    </button>
                  </div>
                  <div className="cp-slide-dice-result-right">
                    {remainingForTab !== null ? (
                      <span className="cp-slide-dice-remaining">
                        남은수&nbsp;:&nbsp;{pad2(remainingForTab)}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className="cp-program-slide-timer-preset cp-program-slide-timer-preset--reset cp-slide-dice-reset-btn"
                      onClick={handleResetTab}
                    >
                      초기화
                    </button>
                  </div>
                </div>
              </div>

              <div className="cp-slide-dice-result-section">
                <div className="cp-slide-dice-result-content">
                  {selectedTab === 'number' && numberCards.length > 0 ? (
                    <div ref={numberGridRef} className="cp-slide-dice-number-grid">
                      {numberCards.map((num, idx) => {
                        const memberName =
                          memberGroupPick.trim() && pickedGroupMembers?.length
                            ? nameForDrawnNumber(num)
                            : '';
                        return (
                          <div
                            key={`${num}-${idx}`}
                            className={
                              'cp-slide-dice-number-item' +
                              (memberName ? ' cp-slide-dice-number-item--with-name' : '')
                            }
                          >
                            <span className="cp-slide-dice-number-item-num">{num}</span>
                            {memberName ? (
                              <span className="cp-slide-dice-number-item-name">{memberName}</span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {selectedTab === 'profile' ? (
                    !pickedGroupMembers?.length ? (
                      <div className="cp-slide-dice-hint">프로필 뽑기를 하려면 멤버 그룹을 선택해 주세요.</div>
                    ) : profileCards.length > 0 ? (
                      <div className="cp-slide-dice-profile-grid">
                        {profileCards.map((member) => (
                          <div key={member.id} className="cp-slide-dice-profile-card">
                            <div className="cp-slide-dice-profile-card-image">
                              <MemberProfileThumb
                                member={member}
                                workspaceId={workspaceId}
                                gradeId={gradeId}
                                alt={displayClassMemberName(member).trim() || '프로필'}
                              />
                            </div>
                            <div className="cp-slide-dice-profile-card-info">
                              {member.number}번{displayClassMemberName(member).trim() ? ` ${displayClassMemberName(member).trim()}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null
                  ) : null}

                  {selectedTab === 'seat' ? (
                    <div className="cp-slide-dice-seat-wrap">
                      {!pickedGroupMembers?.length ? (
                        <div className="cp-slide-dice-hint">자리 뽑기를 하려면 멤버 그룹을 선택해 주세요.</div>
                      ) : seatAssignedItems.length === 0 ? (
                        <div className="cp-slide-dice-hint">
                          {rangeMembers.length === 0
                            ? '시작~끝 번호 범위에 멤버가 없습니다.'
                            : '시작~끝 번호 범위에 자리가 배정된 학생이 없습니다.'}
                        </div>
                      ) : (
                        <>
                          <div className="cp-slide-dice-seat-table-outer">
                            <div className="cp-slide-dice-seat-grid-scroll">
                              <div
                                className="cp-slide-dice-seat-grid"
                                style={{
                                  gridTemplateColumns: diceSeatLayout.colTemplate,
                                  gridTemplateRows: diceSeatLayout.rowTemplate,
                                  ['--dice-seat-scale' as string]: String(seatScale),
                                }}
                              >
                                {diceSeatLayout.assigned.map(({ member, seatRow, seatCol }) => {
                                  const gridRow = seatRow - diceSeatLayout.minRow + 1;
                                  const gridCol = seatCol - diceSeatLayout.minCol + 1;
                                  const visual = getSeatCardVisual(member);
                                  const numLabel = pad2(member.number);
                                  const nameText = displayClassMemberName(member).trim();
                                  const nameLength = Array.from(nameText).length;
                                  const nameFitScale = seatNarrowLayout
                                    ? 1
                                    : Math.max(0.52, Math.min(1, 8 / Math.max(1, nameLength)));
                                  return (
                                    <div
                                      key={member.id}
                                      className="cp-slide-dice-seat-cell"
                                      style={{
                                        gridColumn: `${gridCol} / span 2`,
                                        gridRow: `${gridRow} / span 2`,
                                      }}
                                    >
                                      <div
                                        className={
                                          'cp-slide-dice-seat-card' +
                                          (seatNarrowLayout ? ' is-compact' : '') +
                                          ` is-${visual}`
                                        }
                                        style={{
                                          gap: `${Math.max(2, Math.round(8 * seatScale))}px`,
                                          borderRadius: `${Math.max(4, Math.round(8 * seatScale))}px`,
                                          padding: `${Math.max(2, Math.round(4 * seatScale))}px ${Math.max(2, Math.round(6 * seatScale))}px`,
                                        }}
                                      >
                                        {!seatNarrowLayout ? (
                                          <div
                                            className="cp-slide-dice-seat-profile"
                                            style={{
                                              minWidth: `${Math.max(24, Math.round(40 * seatScale))}px`,
                                              borderRadius: `${Math.max(4, Math.round(6 * seatScale))}px`,
                                            }}
                                          >
                                            <MemberProfileThumb
                                              member={member}
                                              workspaceId={workspaceId}
                                              gradeId={gradeId}
                                              alt={`${displayClassMemberName(member).trim() || '학생'} 프로필`}
                                            />
                                          </div>
                                        ) : null}
                                        <div
                                          className={
                                            'cp-slide-dice-seat-text' + (seatNarrowLayout ? ' is-compact' : '')
                                          }
                                        >
                                          <div
                                            className={
                                              'cp-slide-dice-seat-info' + (seatNarrowLayout ? ' is-compact' : '')
                                            }
                                            style={{
                                              gap: seatNarrowLayout
                                                ? 0
                                                : `${Math.max(2, Math.round(4 * seatScale))}px`,
                                            }}
                                          >
                                            {!seatNarrowLayout ? (
                                              <span
                                                className="cp-slide-dice-seat-number"
                                                style={{
                                                  fontSize: `${Math.max(9, Math.round(17 * seatScale))}px`,
                                                }}
                                              >
                                                {numLabel}
                                              </span>
                                            ) : null}
                                            <span
                                              className={
                                                'cp-slide-dice-seat-name' +
                                                (seatNarrowLayout ? ' is-compact' : '')
                                              }
                                              style={{
                                                fontSize: `${Math.max(10, Math.round(30 * seatScale * nameFitScale))}px`,
                                              }}
                                            >
                                              {nameText}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="cp-slide-dice-seat-rotate-row">
                            <button
                              type="button"
                              className="cp-slide-dice-seat-rotate-btn"
                              onClick={() => setIsSeatRotated((p) => !p)}
                              title="180도 회전"
                              aria-label="자리 배치 180도 회전"
                            >
                              <span className="cp-slide-dice-seat-rotate-icon-wrap">
                                <SeatRotateIcon size={14} />
                              </span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : null}

                  {selectedTab === 'team' && teamResult !== null ? (
                    <div ref={teamResultRef} className="cp-slide-dice-team-result">
                      {isMemberTeamResult(teamResult)
                        ? teamResult.map((team, teamIndex) => (
                            <div key={teamIndex} className="cp-slide-dice-team-block">
                              <div className="cp-slide-dice-team-title">팀 {teamIndex + 1}</div>
                              <div className="cp-slide-dice-team-grid">
                                {team.map((member) => (
                                  <div key={member.id} className="cp-slide-dice-team-member-card">
                                    <div className="cp-slide-dice-team-member-image">
                                      <MemberProfileThumb
                                        member={member}
                                        workspaceId={workspaceId}
                                        gradeId={gradeId}
                                        alt={displayClassMemberName(member).trim() || '프로필'}
                                      />
                                    </div>
                                    <div className="cp-slide-dice-team-member-info">
                                      {member.number}번{displayClassMemberName(member).trim() ? ` ${displayClassMemberName(member).trim()}` : ''}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))
                        : teamResult.map((team, teamIndex) => (
                            <div key={teamIndex} className="cp-slide-dice-team-block">
                              <div className="cp-slide-dice-team-title">팀 {teamIndex + 1}</div>
                              <div className="cp-slide-dice-team-grid">
                                {(team as number[]).map((num) => (
                                  <div key={num} className="cp-slide-dice-team-number">
                                    {num}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>

        {showTeamCountModal ? (
          <div className="cp-slide-dice-team-count-overlay" onClick={() => setShowTeamCountModal(false)}>
            <div
              className="cp-program-slide-timer-modal cp-slide-dice-team-count-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="팀 수 설정"
            >
              <div className="cp-program-slide-timer-modal-header cp-slide-dice-team-count-header">
                <h3 className="cp-program-slide-timer-modal-title">팀 수 설정</h3>
                <button
                  type="button"
                  className="cp-program-slide-timer-modal-close"
                  onClick={() => setShowTeamCountModal(false)}
                  aria-label="닫기"
                >
                  <svg
                    className="cp-program-slide-timer-modal-close-icon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      d="M6 6l12 12M18 6L6 18"
                    />
                  </svg>
                </button>
              </div>
              <div className="cp-slide-dice-team-count-body">
                <label className="cp-slide-dice-team-count-label">팀 수 (최대 {maxTeamCount}팀)</label>
                <input
                  type="number"
                  className="cp-slide-dice-team-count-input"
                  value={teamCount}
                  min={2}
                  max={maxTeamCount}
                  onChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    if (!Number.isNaN(next)) setTeamCount(Math.max(2, Math.min(maxTeamCount, next)));
                  }}
                />
                <div className="cp-slide-dice-team-count-actions">
                  <button
                    type="button"
                    className="cp-slide-dice-step-btn"
                    onClick={() => setTeamCount((v) => Math.max(2, v - 1))}
                    disabled={teamCount <= 2}
                    aria-label="팀 수 줄이기"
                  >
                    <SlideDiceStepIcon kind="minus" />
                  </button>
                  <button type="button" className="cp-slide-dice-icon-btn" onClick={() => setTeamCount(2)} title="초기화">
                    <img src="/reset.svg" alt="" />
                  </button>
                  <button
                    type="button"
                    className="cp-slide-dice-step-btn"
                    onClick={() => setTeamCount((v) => Math.min(maxTeamCount, v + 1))}
                    disabled={teamCount >= maxTeamCount}
                    aria-label="팀 수 늘리기"
                  >
                    <SlideDiceStepIcon kind="plus" />
                  </button>
                </div>
              </div>
              <div className="cp-slide-dice-team-count-footer">
                <button
                  type="button"
                  className="cp-program-slide-timer-preset cp-slide-dice-team-count-btn"
                  onClick={() => setShowTeamCountModal(false)}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="cp-program-slide-timer-preset cp-slide-dice-team-count-btn cp-slide-dice-team-count-btn--primary"
                  onClick={handleDivideTeams}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {!isMaximized ? (
          <>
            <div className="cp-slide-dice-resize cp-slide-dice-resize--nw" onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
            <div className="cp-slide-dice-resize cp-slide-dice-resize--ne" onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
            <div className="cp-slide-dice-resize cp-slide-dice-resize--sw" onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
            <div className="cp-slide-dice-resize cp-slide-dice-resize--se" onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
            {isMinimized ? (
              <>
                <div className="cp-slide-dice-resize cp-slide-dice-resize--n" onMouseDown={(e) => handleResizeMouseDown(e, 'n')} />
                <div className="cp-slide-dice-resize cp-slide-dice-resize--s" onMouseDown={(e) => handleResizeMouseDown(e, 's')} />
                <div className="cp-slide-dice-resize cp-slide-dice-resize--e" onMouseDown={(e) => handleResizeMouseDown(e, 'e')} />
                <div className="cp-slide-dice-resize cp-slide-dice-resize--w" onMouseDown={(e) => handleResizeMouseDown(e, 'w')} />
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default ProgramSlideDiceModal;
